import type {
  DraftsResponse,
  MatchupsResponse,
  DraftSummary,
  HistoryResponse,
  LeagueBundle,
  LineupPlayer,
  LineupsFile,
  TransactionsResponse,
} from '../../shared/types';

/**
 * Follows a traded draft pick forward to the player it actually became.
 *
 * Three datasets have to be joined here — trades, the pick grid, and the draft
 * boards — and their only common key is `roster_id`, which is scoped to a single
 * league year. Sleeper mints a new league every August, so a trade recorded in
 * the 2025 league and the 2026 draft that resolves it speak different
 * numberings. Everything below therefore translates to **user_id** on both
 * sides before matching. The roster ids happen to line up for this league right
 * now, which means a naive implementation would produce correct output today
 * and silently wrong output the first year they do not.
 */

/** roster_id -> user_id, for one league year. */
export type RosterMap = Map<number, string>;

/**
 * Every league year the client knows about, keyed by league id.
 *
 * Assembled from data already on the wire — the current bundle, the history
 * archive and the per-season transaction responses — rather than a new endpoint.
 */
export function buildRosterMaps(
  bundle: LeagueBundle,
  history: HistoryResponse | null,
  transactions: TransactionsResponse[],
): Map<string, RosterMap> {
  const maps = new Map<string, RosterMap>();
  const put = (leagueId: string, rosterId: number, userId: string | null) => {
    if (!userId) return;
    const m = maps.get(leagueId) ?? new Map<number, string>();
    m.set(rosterId, userId);
    maps.set(leagueId, m);
  };

  for (const t of bundle.teams) put(bundle.league.league_id, t.roster_id, t.owner_id);

  for (const s of history?.seasons ?? []) {
    // `placements` is empty until a bracket exists; `regularSeason` always has
    // one row per roster and carries the same owner ids.
    for (const p of s.regularSeason) put(s.league_id, p.roster_id, p.owner_id);
    for (const p of s.placements) put(s.league_id, p.roster_id, p.owner_id);
  }

  for (const t of transactions) {
    for (const [rosterId, userId] of Object.entries(t.rosterOwners)) {
      put(t.leagueId, Number(rosterId), userId);
    }
  }

  return maps;
}

/** One trade that moved the pick, in the numbering of the year it happened. */
export interface PickHop {
  transactionId: string;
  created: number;
  week: number;
  /** League year the trade was recorded in, not the pick's draft year. */
  tradeSeason: string;
  fromUserId: string | null;
  toUserId: string | null;
}

export type PickOutcome =
  | {
      kind: 'drafted';
      pickNo: number;
      round: number;
      draftSlot: number;
      playerId: string;
      playerName: string;
      position: string;
      nflTeam: string | null;
      selectedByUserId: string | null;
    }
  /** The draft for that season has not been held yet. */
  | { kind: 'pending' }
  /** The draft exists but nothing in it matches — see `resolvePick`. */
  | { kind: 'unresolved' };

export interface PickLineage {
  /** `${season}|${round}|${originalUserId}` — stable across league years. */
  key: string;
  season: string;
  round: number;
  /** The manager whose pick it originally was, which is what names it. */
  originalUserId: string;
  /** Chronological, oldest trade first. */
  hops: PickHop[];
  /** Who held it after the last hop. */
  finalOwnerUserId: string;
  outcome: PickOutcome;
}

export const lineageKey = (season: string, round: number, originalUserId: string): string =>
  `${season}|${round}|${originalUserId}`;

/**
 * Finds the player a pick became.
 *
 * A draft pick carries both `draft_slot` (the position in the draft order, i.e.
 * the ORIGINAL owner's seat) and `roster_id` (whoever actually selected). So the
 * pick we want is the one in the right round whose slot maps back to the
 * original owner — no `/traded_picks` call required.
 *
 * `slotToRoster` is 0-indexed while slots are 1-based; getting that wrong shifts
 * the whole board by one seat and resolves every pick to the wrong player.
 */
export function resolvePick(
  season: string,
  round: number,
  originalUserId: string,
  drafts: DraftSummary[],
  rosterMaps: Map<string, RosterMap>,
): PickOutcome {
  const draft = drafts.find((d) => d.season === season);
  if (!draft) return { kind: 'pending' };

  const owners = rosterMaps.get(draft.league_id);
  if (!owners) return { kind: 'unresolved' };

  for (const p of draft.picks) {
    if (p.round !== round) continue;
    const slotRoster = draft.slotToRoster[p.draftSlot - 1];
    if (slotRoster == null) continue;
    if (owners.get(slotRoster) !== originalUserId) continue;
    return {
      kind: 'drafted',
      pickNo: p.pickNo,
      round: p.round,
      draftSlot: p.draftSlot,
      playerId: p.playerId,
      playerName: p.name,
      position: p.position,
      nflTeam: p.nflTeam,
      selectedByUserId: owners.get(p.rosterId) ?? null,
    };
  }

  // A draft short of the traded pick's round — the 2026 rookie draft is three
  // rounds while picks are traded four years out — is a real, expected gap.
  return { kind: 'unresolved' };
}

/**
 * Every pick that has ever changed hands, with its full chain of trades.
 *
 * Scope is deliberately **pick lineage only**: a pick, every trade that moved
 * it, and the player it became. Following that player onward into the next
 * trade, and the picks in *that* trade, has no natural stopping point.
 */
export function buildLineages(
  transactions: TransactionsResponse[],
  drafts: DraftsResponse | null,
  rosterMaps: Map<string, RosterMap>,
): PickLineage[] {
  const hops = new Map<string, { season: string; round: number; user: string; list: PickHop[] }>();

  for (const page of transactions) {
    const owners = page.rosterOwners;
    const user = (rosterId: number): string | null => owners[String(rosterId)] ?? null;

    for (const txn of page.transactions) {
      // Trades of FAAB alone exist in this league and carry no picks at all.
      if (txn.type !== 'trade') continue;

      for (const p of txn.draftPicks) {
        const originalUserId = user(p.originalRosterId);
        // A pick whose original owner has left the league cannot be named.
        if (!originalUserId) continue;

        const key = lineageKey(p.season, p.round, originalUserId);
        const entry =
          hops.get(key) ?? { season: p.season, round: p.round, user: originalUserId, list: [] };
        entry.list.push({
          transactionId: txn.transaction_id,
          created: txn.created,
          week: txn.week,
          tradeSeason: page.season,
          fromUserId: user(p.fromRosterId),
          toUserId: user(p.toRosterId),
        });
        hops.set(key, entry);
      }
    }
  }

  const out: PickLineage[] = [];
  for (const [key, entry] of hops) {
    // A pick can be traded more than once, so the chain is only meaningful in
    // the order the trades actually happened.
    const list = entry.list.sort((a, b) => a.created - b.created);
    out.push({
      key,
      season: entry.season,
      round: entry.round,
      originalUserId: entry.user,
      hops: list,
      finalOwnerUserId: list[list.length - 1]?.toUserId ?? entry.user,
      outcome: resolvePick(entry.season, entry.round, entry.user, drafts?.drafts ?? [], rosterMaps),
    });
  }

  // Resolved trees first, newest draft down — those are the ones with a punch
  // line. Picks still in the future follow in the order they will be used.
  const rank = (l: PickLineage) => (l.outcome.kind === 'drafted' ? 0 : 1);
  return out.sort(
    (a, b) =>
      rank(a) - rank(b) ||
      (rank(a) === 0 ? Number(b.season) - Number(a.season) : Number(a.season) - Number(b.season)) ||
      a.round - b.round,
  );
}

export function lineageIndex(lineages: PickLineage[]): Map<string, PickLineage> {
  return new Map(lineages.map((l) => [l.key, l]));
}

// --- Realized production ---------------------------------------------------

/**
 * One week a player appears in the build-time lineup ledger.
 *
 * `started` is the load-bearing field. Only a starter's points ever reached a
 * team's score, so bench weeks are context -- "acquired him and then sat him" --
 * rather than value, and summing the two together would credit a manager with
 * points nobody scored for them.
 */
/** A `PlayerWeek` that still knows whose row it is, before it is filed by id. */
export type LivePlayerWeek = PlayerWeek & { playerId: string };

export interface PlayerWeek {
  season: string;
  week: number;
  /** Whoever fielded him that week, read straight from the ledger. */
  userId: string;
  started: boolean;
  /** Null while this player's own NFL game is still to come. */
  points: number | null;
}

/**
 * Orders a (season, week) pair against a trade's own stamp.
 *
 * Seasons are four-digit years and a week never exceeds 18, so `season * 100 +
 * week` is monotonic across the league-year boundary: 2025 wk17 is 202517 and
 * 2026 wk1 is 202601. Comparing weeks alone would sort every offseason trade --
 * all of which Sleeper files under week 1 -- ahead of the previous season's
 * playoffs.
 */
const stamp = (season: string, week: number): number => Number(season) * 100 + week;

const round2 = (n: number): number => Math.round(n * 100) / 100;

/**
 * Every appearance of every player in the ledger, keyed by player id.
 *
 * The ledger is ALREADY keyed on user_id (`weeks[week][user_id]`), which is what
 * makes this join safe with no translation step: roster ids mean something only
 * inside one league year, and a player's history crosses those years by
 * definition.
 *
 * Note what this removes the need for. Attribution never consults a trade date
 * -- the ledger records who actually fielded a player each week, so filtering on
 * that manager is a statement about what happened rather than an inference from
 * when a trade cleared.
 */
export function buildProduction(file: LineupsFile | null): Map<string, PlayerWeek[]> {
  const out = new Map<string, PlayerWeek[]>();
  if (!file) return out;

  for (const season of Object.values(file.seasons)) {
    for (const [weekKey, managers] of Object.entries(season.weeks)) {
      const week = Number(weekKey);
      for (const [userId, entry] of Object.entries(managers)) {
        const add = (p: LineupPlayer, started: boolean) => {
          const list = out.get(p[0]) ?? [];
          list.push({ season: season.season, week, userId, started, points: p[3] });
          out.set(p[0], list);
        };
        // A null-pointed row is carried, not dropped. It is the difference
        // between "he sat and scored nothing" and "his game is on Sunday", and
        // only `tally` can tell which phrase a haul deserves.
        for (const p of entry.s) add(p, true);
        for (const p of entry.b) add(p, false);
      }
    }
  }

  for (const list of out.values()) {
    list.sort((a, b) => stamp(a.season, a.week) - stamp(b.season, b.week));
  }
  return out;
}

/**
 * The week in progress, read straight off `/api/matchups`.
 *
 * `lineups.json` is rebuilt once a night, so a Sunday afternoon's points do not
 * reach the trade tab until Monday morning. This is the same information from
 * the live endpoint, shaped into the rows `tally` already understands.
 *
 * `playedTeams` is what makes it honest. Sleeper reports 0 for a player who has
 * kicked off and scored nothing AND for one whose team plays later, so a
 * player whose team is not in that list gets a NULL -- exactly what the nightly
 * sync writes. An empty list therefore yields all-pending rows, which is the
 * correct reading of "the schedule could not be fetched".
 *
 * `teamOf` resolves a player to his NFL team; a defence needs no lookup because
 * its player id already IS the abbreviation.
 */
export function liveWeekRows(
  matchups: MatchupsResponse,
  ownerOf: (rosterId: number) => string | null,
  teamOf: (playerId: string) => string | null,
): LivePlayerWeek[] {
  const done = new Set(matchups.playedTeams);
  const out: LivePlayerWeek[] = [];

  for (const pair of matchups.matchups) {
    for (const side of pair.teams) {
      const userId = ownerOf(side.rosterId);
      if (!userId) continue;
      const add = (playerId: string, points: number, started: boolean) => {
        const team = teamOf(playerId);
        out.push({
          playerId,
          season: matchups.season,
          week: matchups.week,
          userId,
          started,
          points: team && done.has(team) ? points : null,
        });
      };
      // An unfilled slot is the string "0", not a player.
      for (const p of side.starters) if (p.playerId && p.playerId !== '0') add(p.playerId, p.points, true);
      for (const p of side.bench) add(p.playerId, p.points, false);
    }
  }

  return out;
}

/**
 * Overlays the live week onto the nightly ledger, ADDING information only.
 *
 * The asymmetry is the whole design. A live row that has been played replaces
 * whatever the ledger holds, because it is strictly fresher. A live row still
 * PENDING is inserted only where the ledger has nothing -- otherwise a failed
 * schedule fetch, or a player whose team is mid-game, would blank a result the
 * nightly sync had already captured correctly. So the merge is monotone: it can
 * turn "unknown" into "known", never the reverse.
 *
 * Keyed on player id plus manager, because the same player can appear for two
 * managers in one week only if a trade cleared mid-week -- in which case both
 * rows are real and the window in `tally` sorts out which hop owns them.
 */
export function mergeLiveWeek(
  production: Map<string, PlayerWeek[]>,
  rows: LivePlayerWeek[] | null,
): Map<string, PlayerWeek[]> {
  if (!rows || rows.length === 0) return production;

  const out = new Map(production);
  for (const { playerId, ...row } of rows) {
    const existing = out.get(playerId) ?? [];
    const at = existing.findIndex(
      (w) => w.season === row.season && w.week === row.week && w.userId === row.userId,
    );
    if (at === -1) {
      out.set(
        playerId,
        [...existing, row].sort((a, b) => stamp(a.season, a.week) - stamp(b.season, b.week)),
      );
      continue;
    }
    // Present already: only a played row is worth replacing it with.
    if (row.points === null) continue;
    const merged = [...existing];
    merged[at] = row;
    out.set(playerId, merged);
  }
  return out;
}

/**
 * The last week the ledger has any result for, as a `stamp`.
 *
 * Load-bearing for honesty rather than for arithmetic. An asset acquired after
 * this point has scored nothing AND could not have -- a 2026 rookie drafted in
 * an August with no games played is not a bust, he is unplayed. Collapsing the
 * two into "0.00" makes a picks-for-player trade read as a blowout for the side
 * that got the player, which is the same null-versus-zero trap the projection
 * ledger already documents. Returns -Infinity for an empty ledger, so nothing is
 * scoreable rather than everything.
 */
export function lastPlayedStamp(production: Map<string, PlayerWeek[]>): number {
  let last = -Infinity;
  for (const weeks of production.values()) {
    for (const w of weeks) {
      // Only a PLAYED row moves the horizon. A week that has kicked off is in
      // the ledger from its first whistle, and counting its not-yet-played
      // rows here would declare every asset in it settled -- which is exactly
      // the claim this function exists to refuse.
      if (w.points === null) continue;
      const at = stamp(w.season, w.week);
      if (at > last) last = at;
    }
  }
  return last;
}

/** What one manager got out of one player over one window of ownership. */
export interface Haul {
  starts: number;
  /** Points scored while STARTED. Bench points never reached anyone's score. */
  points: number;
  /**
   * Weeks he sat on their bench instead.
   *
   * UNDERCOUNTS, unlike `benchPoints` below. The ledger drops a bench player
   * with neither a projection nor any points, since he cannot move a single
   * decision-quality metric -- so a benched zero is often simply absent. Every
   * player who SCORED is kept, which is why the points are exact and the weeks
   * are not.
   */
  benched: number;
  /**
   * Points scored while on their bench.
   *
   * Deliberately a separate field rather than folded into `points`: these never
   * reached anyone's score, so the ledger totals and the spread must keep
   * counting starts only. What they do measure is the asset itself -- a player
   * can be a fine acquisition and a badly used one, and collapsing the two
   * hides which.
   */
  benchPoints: number;
  /**
   * Rows in the window whose player has not kicked off yet.
   *
   * The trade-level horizon cannot answer this. A trade made in a week that has
   * STARTED is past that horizon, so the trade is settled -- while a player
   * inside it whose team plays on Sunday is not. Measured on 2026 wk1: Romeo
   * Doubs played Thursday and scored 0.00, a real fact, while MarShawn Lloyd's
   * Green Bay had not taken the field. Both read "0.00" in Sleeper's payload
   * and only one of them is a number. Without this counter the second renders
   * as "no starts", which claims the ledger looked and found nothing.
   */
  pending: number;
}

const emptyHaul = (): Haul => ({
  starts: 0,
  points: 0,
  benched: 0,
  benchPoints: 0,
  pending: 0,
});

/** True when nothing in this haul has been played, but something is coming. */
export const haulIsPending = (h: Haul): boolean =>
  h.pending > 0 && h.starts === 0 && h.benched === 0;

/**
 * Points a player scored for one manager inside a half-open window of weeks.
 *
 * The window exists for exactly one case: a player who ROUND-TRIPS, traded away
 * and later reacquired by the same manager. Without it the first trade would
 * also be credited with the second spell. Every other case is already settled by
 * the `userId` test, which means a window that is wrong can only ever
 * under-credit -- never credit the wrong manager.
 *
 * Two trades in the same week collapse to an empty window, and that is correct
 * rather than a gap: a player flipped twice in one offseason never played for
 * the manager in the middle.
 */
function tally(
  weeks: PlayerWeek[] | undefined,
  userId: string | null,
  from: number,
  to: number,
): Haul {
  const h = emptyHaul();
  if (!weeks || !userId) return h;

  for (const w of weeks) {
    if (w.userId !== userId) continue;
    const at = stamp(w.season, w.week);
    if (at < from || at >= to) continue;
    // A game still to come is neither a start nor a benching yet -- counting it
    // either way reports a decision whose outcome does not exist.
    if (w.points === null) {
      h.pending += 1;
      continue;
    }
    if (w.started) {
      h.starts += 1;
      h.points += w.points;
    } else {
      h.benched += 1;
      h.benchPoints += w.points;
    }
  }

  h.points = round2(h.points);
  h.benchPoints = round2(h.benchPoints);
  return h;
}

// --- Player lineage --------------------------------------------------------

/** One trade that moved a player, in the numbering of the year it happened. */
export interface PlayerHop {
  transactionId: string;
  created: number;
  week: number;
  /** League year the trade was recorded in, not necessarily one he played in. */
  tradeSeason: string;
  fromUserId: string | null;
  toUserId: string | null;
  /** What the receiving manager got out of him before the next trade moved him. */
  haul: Haul;
  /**
   * False when the trade postdates every week the ledger has played. The haul is
   * then genuinely unknown rather than zero, and must not be printed as 0.00.
   */
  scoreable: boolean;
}

/**
 * A player, every trade that has moved him, and what each side got.
 *
 * The pick counterpart above terminates naturally -- a pick ends at a draft
 * selection, which is a leaf. A player chain does not, which is why the scope
 * here is the player alone: the trades he was IN, never the other assets that
 * moved alongside him. Following those would pull in their picks, and their
 * picks' players, until one trade spans the league's whole history.
 */
export interface PlayerLineage {
  playerId: string;
  /** Chronological, oldest trade first. */
  hops: PlayerHop[];
  /** Who gave him up in the first trade on record. */
  firstOwnerUserId: string | null;
  finalOwnerUserId: string | null;
  /** Every start in the ledger, for anyone. Context the hauls are read against. */
  careerStarts: number;
  careerPoints: number;
  /** Points scored from anyone's bench, over the same span. */
  careerBenchPoints: number;
}

export function buildPlayerLineages(
  transactions: TransactionsResponse[],
  production: Map<string, PlayerWeek[]>,
): PlayerLineage[] {
  const moves = new Map<string, PlayerHop[]>();

  for (const page of transactions) {
    const owners = page.rosterOwners;
    const user = (rosterId: number | undefined): string | null =>
      rosterId == null ? null : owners[String(rosterId)] ?? null;

    for (const txn of page.transactions) {
      // Pick-only and FAAB-only trades both exist in this league and carry no
      // `adds` at all, so this loop simply skips them.
      if (txn.type !== 'trade') continue;

      for (const [playerId, toRosterId] of Object.entries(txn.adds)) {
        // Sleeper files a traded player in BOTH maps -- `adds` names the roster
        // receiving him and `drops` the one giving him up -- so the other side
        // of the move needs no search. Verified against a 2025 two-for-two.
        const list = moves.get(playerId) ?? [];
        list.push({
          transactionId: txn.transaction_id,
          created: txn.created,
          week: txn.week,
          tradeSeason: page.season,
          fromUserId: user(txn.drops[playerId]),
          toUserId: user(toRosterId),
          haul: emptyHaul(),
          scoreable: false,
        });
        moves.set(playerId, list);
      }
    }
  }

  const horizon = lastPlayedStamp(production);
  const out: PlayerLineage[] = [];
  for (const [playerId, unsorted] of moves) {
    // A player can be traded more than once, and the windows below are only
    // meaningful in the order the trades actually happened.
    const hops = unsorted.sort((a, b) => a.created - b.created);
    const weeks = production.get(playerId);

    for (let i = 0; i < hops.length; i += 1) {
      const next = hops[i + 1];
      const from = stamp(hops[i].tradeSeason, hops[i].week);
      hops[i].haul = tally(
        weeks,
        hops[i].toUserId,
        from,
        next ? stamp(next.tradeSeason, next.week) : Infinity,
      );
      hops[i].scoreable = from <= horizon;
    }

    let careerStarts = 0;
    let careerPoints = 0;
    let careerBenchPoints = 0;
    for (const w of weeks ?? []) {
      // A week he has not played is not part of anyone's career total yet.
      if (w.points === null) continue;
      if (!w.started) {
        careerBenchPoints += w.points;
        continue;
      }
      careerStarts += 1;
      careerPoints += w.points;
    }

    out.push({
      playerId,
      hops,
      firstOwnerUserId: hops[0]?.fromUserId ?? null,
      finalOwnerUserId: hops[hops.length - 1]?.toUserId ?? null,
      careerStarts,
      careerPoints: round2(careerPoints),
      careerBenchPoints: round2(careerBenchPoints),
    });
  }

  return out.sort((a, b) => b.careerPoints - a.careerPoints || b.hops.length - a.hops.length);
}

/** Keyed `transactionId|playerId` -> what that trade produced for the receiver. */
export function haulIndex(lineages: PlayerLineage[]): Map<string, Haul> {
  const m = new Map<string, Haul>();
  for (const l of lineages) {
    for (const h of l.hops) m.set(`${h.transactionId}|${l.playerId}`, h.haul);
  }
  return m;
}

// --- Trade ledger ----------------------------------------------------------

export type TradeAsset =
  | { kind: 'player'; playerId: string; haul: Haul; scoreable: boolean }
  | {
      kind: 'pick';
      season: string;
      round: number;
      originalUserId: string | null;
      outcome: PickOutcome;
      /**
       * False while the pick still owes this side something the ledger cannot
       * see yet: an undrafted pick, or one drafted into a season that has not
       * kicked off. A pick flipped onward is scoreable -- it owes nothing,
       * because what it turned into is the next trade's row.
       */
      scoreable: boolean;
      /**
       * The player this pick became, whenever the draft has happened -- even if
       * this side flipped it onward before the draft.
       *
       * WHAT A PICK BECAME AND WHO GETS CREDIT FOR IT ARE DIFFERENT QUESTIONS.
       * An earlier version answered only the second, so a flipped pick rendered
       * as "traded on" and the player disappeared from the page entirely, even
       * though the draft board knows exactly who he is. A pick's fate is a fact
       * about the pick and belongs on every row it passes through; `credited` is
       * the separate thing that keeps the arithmetic honest.
       */
      became: {
        playerId: string;
        name: string;
        position: string;
        /** Who actually made the selection. Differs from this side when flipped. */
        selectedByUserId: string | null;
        /** Production for the manager who SELECTED him, since that draft. */
        haul: Haul;
        /**
         * False when that draft's season has no games yet, so the haul above
         * is unknown rather than zero. Distinct from the asset's own
         * `scoreable`, which is true for a flipped pick because it owes THIS
         * side nothing -- reading that one here would print an unplayed rookie
         * as "no starts".
         */
        playedSeason: boolean;
        /**
         * Whether this side's `points` include that haul. False when they
         * flipped the pick onward: they never had the player, and what they did
         * get is the next trade's row. This is the rule that gives the chain its
         * stopping point, and it is unchanged -- only the display widened.
         */
        credited: boolean;
      } | null;
    }
  | { kind: 'faab'; amount: number };

export interface TradeSide {
  userId: string | null;
  rosterId: number;
  assets: TradeAsset[];
  /** Realized starter points from everything received, players and picks alike. */
  points: number;
  /**
   * The same haul counting bench weeks too. Never feeds `spread` -- these
   * points did not reach the score, so a comparison built on them would be
   * scoring what a manager COULD have had, not what they got.
   */
  benchPoints: number;
  starts: number;
  /** Assets that cannot be scored yet. While any remain, `points` is a floor. */
  pending: number;
}

export interface TradeRecord {
  transactionId: string;
  created: number;
  week: number;
  /** League year it was recorded in. Offseason trades all file under week 1. */
  season: string;
  sides: TradeSide[];
  /**
   * True when every asset on every side has at least had the chance to score.
   * False means a pick is still undrafted, or was drafted into a season nobody
   * has played -- the case that makes a picks-for-player trade look one-sided
   * when it has simply not resolved.
   */
  complete: boolean;
  /**
   * Points between the best and worst side, or null unless the trade is
   * `complete` and someone has started something. Deliberately not called a
   * margin of victory: every row is a running total, and FAAB carries no points
   * at all.
   */
  spread: number | null;
}

/**
 * Every trade, with the realized points each side has drawn from it TO DATE.
 *
 * Three deliberate limits, none of which the arithmetic can fix:
 *
 * 1. Only starter points count. Bench production never reached a score.
 * 2. A pick is credited to a side only if that side also made the selection.
 * 3. Nothing here is final. A player acquired last August is still scoring, so
 *    every row is a running total and the UI has to say so.
 */
export function buildTradeLedger(
  transactions: TransactionsResponse[],
  drafts: DraftsResponse | null,
  rosterMaps: Map<string, RosterMap>,
  production: Map<string, PlayerWeek[]>,
  lineages: PlayerLineage[],
): TradeRecord[] {
  const hauls = haulIndex(lineages);
  const horizon = lastPlayedStamp(production);
  const out: TradeRecord[] = [];

  for (const page of transactions) {
    const owners = page.rosterOwners;
    const user = (rosterId: number | undefined): string | null =>
      rosterId == null ? null : owners[String(rosterId)] ?? null;

    for (const txn of page.transactions) {
      if (txn.type !== 'trade') continue;

      const tradedAt = stamp(page.season, txn.week);

      const sides: TradeSide[] = txn.rosterIds.map((rosterId) => {
        const userId = user(rosterId);
        const assets: TradeAsset[] = [];

        for (const [playerId, toRosterId] of Object.entries(txn.adds)) {
          if (toRosterId !== rosterId) continue;
          assets.push({
            kind: 'player',
            playerId,
            haul: hauls.get(`${txn.transaction_id}|${playerId}`) ?? emptyHaul(),
            // Past the horizon AND actually on the field. The second half only
            // ever bites inside a week that has kicked off, which is the one
            // place the trade and the player can disagree about being settled.
            scoreable:
              tradedAt <= horizon &&
              !haulIsPending(hauls.get(`${txn.transaction_id}|${playerId}`) ?? emptyHaul()),
          });
        }

        for (const p of txn.draftPicks) {
          if (p.toRosterId !== rosterId) continue;
          const originalUserId = user(p.originalRosterId);
          const outcome: PickOutcome = originalUserId
            ? resolvePick(p.season, p.round, originalUserId, drafts?.drafts ?? [], rosterMaps)
            : { kind: 'unresolved' };

          // The pick's fate is resolved for everyone; only the CREDIT is
          // conditional. `haul` is always the selecting manager's production,
          // so a flipped pick can still say what it turned into and for whom.
          const became =
            outcome.kind === 'drafted'
              ? {
                  playerId: outcome.playerId,
                  name: outcome.playerName,
                  position: outcome.position,
                  selectedByUserId: outcome.selectedByUserId,
                  // From the start of the draft's season: a rookie draft runs
                  // before week 1, so every week of that season is his.
                  haul: tally(
                    production.get(outcome.playerId),
                    outcome.selectedByUserId,
                    stamp(p.season, 0),
                    Infinity,
                  ),
                  playedSeason: stamp(p.season, 0) <= horizon,
                  credited: outcome.selectedByUserId === userId,
                }
              : null;

          assets.push({
            kind: 'pick',
            season: p.season,
            round: p.round,
            originalUserId,
            outcome,
            became,
            // A pick this side flipped owes them nothing -- what they got for it
            // is a row of its own -- so it is settled even though it scored them
            // nothing. Only a pick they still hold, into a season nobody has
            // played, is genuinely outstanding.
            scoreable: outcome.kind === 'drafted' && (!became?.credited || became.playedSeason),
          });
        }

        for (const f of txn.faab) {
          if (f.to !== rosterId) continue;
          assets.push({ kind: 'faab', amount: f.amount });
        }

        let points = 0;
        let benchPoints = 0;
        let starts = 0;
        let pending = 0;
        for (const a of assets) {
          if (a.kind === 'player') {
            points += a.haul.points;
            benchPoints += a.haul.benchPoints;
            starts += a.haul.starts;
          } else if (a.kind === 'pick' && a.became?.credited) {
            points += a.became.haul.points;
            benchPoints += a.became.haul.benchPoints;
            starts += a.became.haul.starts;
          }
          if (a.kind !== 'faab' && !a.scoreable) pending += 1;
        }

        return {
          userId,
          rosterId,
          assets,
          points: round2(points),
          benchPoints: round2(benchPoints),
          starts,
          pending,
        };
      });

      // A spread across sides only means something once BOTH have had the
      // chance to score. Otherwise it reports the unplayed side as a zero.
      const complete = sides.length > 0 && sides.every((s) => s.pending === 0);
      const scored = complete && sides.some((s) => s.starts > 0);
      out.push({
        transactionId: txn.transaction_id,
        created: txn.created,
        week: txn.week,
        season: page.season,
        sides,
        complete,
        spread: scored
          ? round2(
              Math.max(...sides.map((s) => s.points)) - Math.min(...sides.map((s) => s.points)),
            )
          : null,
      });
    }
  }

  return out.sort((a, b) => b.created - a.created);
}
